<template>
  <div class="shrink-0 border-t p-4">
    <div v-if="pendingUploads.length > 0" class="mb-3 flex flex-wrap gap-2">
      <UploadedFile v-for="upload in pendingUploads" :key="upload.id || upload.uuid" :file="upload" @deleted="removePendingUpload" @error="handleUploadError" />
    </div>

    <form class="flex items-end gap-3" @submit.prevent="submitMessage">
      <UploadButton ref="uploadButtonRef" :multiple="true" label="Attach files" uploading-label="Uploading..." :disabled="messageStore.isSendingMessage" @upload-start="handleUploadStart" @upload-finish="handleUploadFinish" @uploaded="handleUploaded" @error="handleUploadError">
        <template #default="{ isUploading }">
          <LoaderCircle v-if="isUploading" class="h-4 w-4 animate-spin" />
          <Paperclip v-else class="h-4 w-4" />
          <span class="sr-only">{{ isUploading ? 'Uploading files' : 'Attach files' }}</span>
        </template>
      </UploadButton>

      <Textarea v-model="body" class="min-h-11 flex-1 resize-none" rows="1" placeholder="Write a message..." @keydown.enter.exact.prevent="submitMessage" @paste="handlePaste" />
      <Button v-if="!hideSendButton" type="submit" :disabled="!canSend" :loading="messageStore.isSendingMessage">
        <Send class="h-4 w-4" />
        Send
      </Button>
    </form>
    <p v-if="sendMessageError" class="mt-2 text-xs text-destructive">
      {{ sendMessageError }}
    </p>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { LoaderCircle, Paperclip, Send } from 'lucide-vue-next'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { Textarea } from '../../../../../../../resources/js/components/ui/textarea/index.js'
import UploadButton from '../../../../../uploadable/resources/js/components/UploadButton.vue'
import UploadedFile from '../../../../../uploadable/resources/js/components/UploadedFile.vue'
import { useMessageStore } from '../../stores/messageStore.js'

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  hideSendButton: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['messageSendStarted', 'messageSent', 'messageSendFailed'])

const messageStore = useMessageStore()
const uploadButtonRef = ref(null)
const body = ref('')
const pendingUploads = ref([])
const isUploadingFiles = ref(false)
const sendMessageError = ref('')

const pendingUploadIds = computed(() => {
  return pendingUploads.value.map((upload) => Number(upload.id)).filter((uploadId) => Number.isInteger(uploadId) && uploadId > 0)
})

const canSend = computed(() => {
  return Boolean((body.value.trim() || pendingUploadIds.value.length > 0) && !messageStore.isSendingMessage && !isUploadingFiles.value)
})

const submitMessage = async () => {
  if (!canSend.value) {
    return
  }

  const messageBody = body.value.trim()
  sendMessageError.value = ''
  emit('messageSendStarted')

  try {
    const payload = {
      type: resolveMessageType(messageBody),
    }

    if (messageBody !== '') {
      payload.body = messageBody
    }

    if (pendingUploadIds.value.length > 0) {
      payload.payload = {
        upload_ids: pendingUploadIds.value,
      }
    }

    const messagePromise = messageStore.sendMessage(props.conversationId, payload, {
      optimisticUploads: [...pendingUploads.value],
    })

    body.value = ''
    pendingUploads.value = []
    const message = await messagePromise
    emit('messageSent', message)
  } catch (error) {
    sendMessageError.value = errorMessageFrom(error, 'Unable to send message.')
    emit('messageSendFailed', sendMessageError.value)
  }
}

const resolveMessageType = (messageBody) => {
  if (pendingUploadIds.value.length === 0) {
    return 'text'
  }

  if (messageBody !== '' || pendingUploadIds.value.length > 1) {
    return 'mixed'
  }

  const upload = pendingUploads.value[0]

  if (String(upload?.mime_type ?? '').toLowerCase().startsWith('image/')) {
    return 'image'
  }

  return 'file'
}

const handleUploadStart = () => {
  sendMessageError.value = ''
  isUploadingFiles.value = true
}

const handleUploadFinish = () => {
  isUploadingFiles.value = false
}

const handlePaste = async (event) => {
  const clipboardItems = Array.from(event.clipboardData?.items ?? [])
  const pastedImages = clipboardItems
    .filter((item) => item.kind === 'file' && item.type.toLowerCase().startsWith('image/'))
    .map((item) => item.getAsFile())
    .filter((image) => image !== null)

  if (pastedImages.length === 0) {
    return
  }

  event.preventDefault()
  await uploadButtonRef.value?.uploadFiles(pastedImages).catch(() => null)
}

const handleUploaded = (payload) => {
  const uploadedItems = Array.isArray(payload) ? payload : [payload]
  const uploadById = new Map(pendingUploads.value.map((upload) => [Number(upload.id), upload]))

  uploadedItems
    .filter((upload) => upload?.id)
    .forEach((upload) => {
      uploadById.set(Number(upload.id), upload)
    })

  pendingUploads.value = Array.from(uploadById.values())
}

const removePendingUpload = (deletedFile) => {
  pendingUploads.value = pendingUploads.value.filter((upload) => Number(upload.id) !== Number(deletedFile?.id))
}

const handleUploadError = (error) => {
  sendMessageError.value = errorMessageFrom(error, 'Unable to upload selected file.')
}

const errorMessageFrom = (error, fallback) => {
  const validationErrors = error?.response?.data?.errors

  if (validationErrors) {
    const messages = Object.values(validationErrors).flat().filter(Boolean)

    if (messages.length > 0) {
      return messages.join(' ')
    }
  }

  return error?.response?.data?.message || error?.message || fallback
}
</script>
